import { memo, useCallback, useMemo, useState } from 'react';
import { Upload } from '@divlab/divanui';
import { nanoid } from 'nanoid';

import validationSchema from './schema';
import * as ModalWindows from '@Stores/ModalWindows';
import { useDeps } from '@Contexts/DI';
import useMedias from '@Hooks/useMedias';
import useTranslation from '@Queries/useTranslation';
import Form from '@UI/Form';
import InputPlaceholder from '@UI/InputPlaceholder';
import Button from '@UI/Button';
import MainSelect from '@UI/MainSelect';
import Link from '@Navigation/Link';
import InputPhone from '@Components/InputPhone';
import Tab from '../Tab';
import Price from './elems/Price';
import styles from './ExpressDesignForm.module.css';

import type { FC, HTMLAttributes } from 'react';
import type { ApiResponse } from '@Types/Api';
import type { TariffData } from '@Pages/PageExpressDesign/type';
import type { TabData } from '../Tab';

interface ExpressDesignFormProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  tariffs: TariffData[];
  selectedTariff?: TariffData;
  selectedType: string;
  onSelectTariff?: (tariffId?: string) => void;
  onChangeType?: (type: string) => void;
  ofertaLink: string;
}

interface SelectItemTypesData {
  title: string;
  id: string;
  name: string;
}

const ExpressDesignForm: FC<ExpressDesignFormProps> = (props) => {
  const { tariffs, selectedTariff, selectedType, onSelectTariff, onChangeType, ofertaLink } = props;
  const { t } = useTranslation();
  const { isMobileM } = useMedias();
  const [formKey, setFormKey] = useState<string>(nanoid());
  const [uploadError, setUploadError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const { analytics } = useDeps();

  const formatTabs: TabData[] = useMemo(() => {
    return tariffs.map((tariff) => {
      return { id: tariff.id.toString(), label: tariff.title, tooltip: tariff.advantages };
    });
  }, [tariffs]);

  const selectItems = useMemo(() => {
    return selectedTariff.tabs.map((tab) => ({
      id: tab.id,
      title: tab.name,
      name: tab.name,
      selected: tab.name === selectedType,
      ...(tab.price && { price: { value: tab.price.value, currency: tab.price.currency } }),
    }));
  }, [selectedType, selectedTariff]);

  const handleChangeType = useCallback(
    (_e, item: SelectItemTypesData[]) => {
      if (onChangeType) onChangeType(item[0].title);
    },
    [onChangeType],
  );

  const transformDataBeforeSubmit = useCallback(
    (data) => {
      return {
        ...data,
        service: selectedTariff ? selectedTariff.title : '',
        place: selectedType,
      };
    },
    [selectedTariff, selectedType],
  );

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    setLoading(true);
  }, []);

  const handleChangeUploadError = useCallback((e: null, err: string) => {
    setUploadError(err);
  }, []);

  const handleError = useCallback(() => {
    setLoading(false);

    ModalWindows.open('Info', { view: 'error', variant: 'unknown-error' });
  }, []);

  const handleResponse = useCallback(
    (response: ApiResponse, data) => {
      setLoading(false);

      if (response.ok === true) {
        analytics.dispatchEvent('expressDesignForm.send', {
          label: selectedTariff ? selectedTariff.title : '',
          inputValues: data,
        });

        ModalWindows.open('Info', { view: 'success', variant: 'manager-callback' });
        setFormKey(nanoid());
      } else {
        handleError();
      }
    },
    [analytics, selectedTariff, handleError],
  );

  const handleClickTariff = (tab: TabData) => {
    if (onSelectTariff) onSelectTariff(tab.id.toString());
  };

  return (
    <Form
      key={formKey}
      action='/backend/b2b/send-letter?form=express-design'
      name='express-design'
      validationSchemaBuilder={validationSchema}
      disabled={!!uploadError}
      onCriticalError={handleError}
      onSubmit={handleSubmit}
      onResponse={handleResponse}
      transformDataBeforeSubmit={transformDataBeforeSubmit}
    >
      <div className={styles.wrapper}>
        <InputPlaceholder className={styles.name} name='name' placeholder={t('form.name')} />
        <InputPhone name='phone' placeholder={t('form.telephone')} mode='redesign' />
      </div>
      <InputPlaceholder className={styles.email} name='email' placeholder={t('form.email')} />
      <div className={styles.tariffs}>
        <div className={styles.text}>{t('form.choose-tariff')}</div>
        <div className={styles.tabs}>
          {formatTabs.map((tab) => {
            return (
              <Tab
                key={tab.id}
                item={tab}
                onChangeTab={handleClickTariff}
                idSelectedTab={selectedTariff?.id.toString()}
                tooltip={tab.tooltip}
                isHiddenTooltip={isMobileM}
              />
            );
          })}
        </div>
      </div>
      <MainSelect
        key={selectedTariff.variant}
        className={styles.select}
        items={selectItems}
        title={selectedTariff.typeName}
        name='place'
        onChangeSelected={handleChangeType}
      />

      <Upload
        className={styles.files}
        view='vertical'
        name='file[]'
        maxSizePerFile={10 * 1024 * 1024}
        accept='.png, .jpg, .pdf'
        title={t('form.upload.title')}
        description={t('form.upload.description')}
        maxCount={10}
        multiple
        theme='light'
        onChangeError={handleChangeUploadError}
      />
      <div className={styles.btnWrapper}>
        <Price
          selectedType={selectedType}
          selectedTariff={selectedTariff}
          className={styles.price}
        />
        <Button type='submit' className={styles.button} waiting={loading}>
          {t('send-request')}
        </Button>
      </div>

      <div className={styles.info}>
        {t('privacy-policy.before')}{' '}
        <Link to={ofertaLink} view='native' target='_blank'>
          {t('privacy-policy.link-oferta')}
        </Link>{' '}
        {t('privacy-policy.and')}{' '}
        <Link to='/static-page/privacy-policy' view='native' target='_blank'>
          {t('privacy-policy.link')}
        </Link>
      </div>
    </Form>
  );
};

export default memo(ExpressDesignForm);
